import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { Button, Input } from "components/atoms";
import { FormGrid, FlexWrapper, FormInput } from "components/molecules";
import Dropdown from "components/molecules/dropdown/Dropdown";
import DeliveryStatusNotificationPopup from "components/molecules/popup/delivery-status-notification/DeliveryStatusNotificationPopup";
import { PiGear } from "react-icons/pi";
import { INotificationSettingsForm } from "services/configuration/notifications/types";
import {
  DELIVERY_ERROR_STATUS_IDS,
  DELIVERY_STATUS_NOTIFICATION_TYPE_ID,
} from "services/configuration/notifications/constants";
import { BUTTON_VARIANT } from "types/components/button/button.interface";

const NotificationContent = () => {
  const { values, status, setFieldValue } =
    useFormikContext<INotificationSettingsForm>();
  const { t } = useTranslation();

  const mockedNotifications = values.mockedNotifications || {};

  const handleSelectAll = (domain: string, isSelected: boolean) => {
    const updatedNotifications = {
      ...mockedNotifications,
      [domain]: {
        ...mockedNotifications[domain],
        notifications: mockedNotifications[domain].notifications.map(
          (notification) => {
            const updatedNotification = {
              ...notification,
              isSelected,
            };

            if (
              isSelected &&
              notification.id === DELIVERY_STATUS_NOTIFICATION_TYPE_ID &&
              !notification.deliveryStatusIds?.length
            ) {
              updatedNotification.deliveryStatusIds = [
                ...DELIVERY_ERROR_STATUS_IDS,
              ];
            }

            return updatedNotification;
          }
        ),
      },
    };

    setFieldValue("mockedNotifications", updatedNotifications);
  };

  const handleNotificationChange = (
    domain: string,
    notificationId: number,
    field: string,
    value: boolean
  ) => {
    const updatedNotifications = {
      ...mockedNotifications,
      [domain]: {
        ...mockedNotifications[domain],
        notifications: mockedNotifications[domain].notifications.map(
          (notification) => {
            if (notification.id !== notificationId) {
              return notification;
            }

            const updatedNotification = {
              ...notification,
              [field]: value,
            };

            if (
              notificationId === DELIVERY_STATUS_NOTIFICATION_TYPE_ID &&
              field === "isSelected" &&
              value &&
              !notification.deliveryStatusIds?.length
            ) {
              updatedNotification.deliveryStatusIds = [
                ...DELIVERY_ERROR_STATUS_IDS,
              ];
            }

            return updatedNotification;
          }
        ),
      },
    };

    setFieldValue("mockedNotifications", updatedNotifications);
  };

  const handleDeliveryStatusIdsChange = (
    domain: string,
    notificationId: number,
    deliveryStatusIds: string[] | null
  ) => {
    const updatedNotifications = {
      ...mockedNotifications,
      [domain]: {
        ...mockedNotifications[domain],
        notifications: mockedNotifications[domain].notifications.map(
          (notification) =>
            notification.id === notificationId
              ? { ...notification, deliveryStatusIds }
              : notification
        ),
      },
    };

    setFieldValue("mockedNotifications", updatedNotifications);
  };

  const sortedDomains = Object.keys(mockedNotifications).sort((a, b) => {
    if (a === "-1") return 1;
    if (b === "-1") return -1;
    const aTranslated = t(`notifications.domains.${a}`);
    const bTranslated = t(`notifications.domains.${b}`);
    return aTranslated.localeCompare(bTranslated);
  });

  const getSortedNotifications = (domain: string) => {
    return [...mockedNotifications[domain].notifications].sort((a, b) => {
      const aTranslated = t(`notifications.${a.id}`);
      const bTranslated = t(`notifications.${b.id}`);
      return aTranslated.localeCompare(bTranslated);
    });
  };

  return (
    <FlexWrapper>
      {sortedDomains.map((domain) => {
        if (domain === "-1") {
          return null;
        }
        return (
          <Dropdown
            key={domain}
            title={t(`notifications.domains.${domain}`)}
            extraElement={
              <Input.Switch
                label=""
                onChange={(checked) => handleSelectAll(domain, checked)}
                checked={mockedNotifications[domain].notifications.some(
                  (notification) => notification.isSelected
                )}
                className={`${
                  status?.displayMode
                    ? "pointer-events-none"
                    : "pointer-events-auto"
                }`}
              />
            }
          >
            <FormGrid>
              {getSortedNotifications(domain).map((notification) => {
                const originalIndex = mockedNotifications[
                  domain
                ].notifications.findIndex((n) => n.id === notification.id);

                return (
                  <div className="flex flex-col" key={notification.id}>
                    <FormInput.Switch
                      name={`mockedNotifications.${domain}.notifications.${originalIndex}.isSelected`}
                      label={t(`notifications.${notification.id}`)}
                      checked={notification.isSelected}
                      onChange={(checked: boolean) =>
                        handleNotificationChange(
                          domain,
                          notification.id,
                          "isSelected",
                          checked
                        )
                      }
                      className={`${
                        status?.displayMode
                          ? "pointer-events-none"
                          : "pointer-events-auto"
                      }`}
                    />
                    <FormInput.Switch
                      name={`mockedNotifications.${domain}.notifications.${originalIndex}.sendEmail`}
                      label={t(`notifications.sendEmail`)}
                      checked={notification.sendEmail}
                      onChange={(checked: boolean) =>
                        handleNotificationChange(
                          domain,
                          notification.id,
                          "sendEmail",
                          checked
                        )
                      }
                      disabled={!notification.isSelected}
                      className={`${
                        status?.displayMode
                          ? "pointer-events-none"
                          : "pointer-events-auto"
                      }`}
                    />
                    {notification.id === DELIVERY_STATUS_NOTIFICATION_TYPE_ID &&
                      notification.isSelected && (
                        <DeliveryStatusNotificationPopup
                          deliveryStatusIds={
                            notification.deliveryStatusIds ?? []
                          }
                          onSave={(deliveryStatusIds) =>
                            handleDeliveryStatusIdsChange(
                              domain,
                              notification.id,
                              deliveryStatusIds
                            )
                          }
                          trigger={
                            <Button isClear size="compact" variant={BUTTON_VARIANT.submit}>
                              <Button.Text
                                text={t("notifications.select-delivery-status")}
                              />
                              <Button.Icon icon={<PiGear />} />
                            </Button>
                          }
                        />
                      )}
                  </div>
                );
              })}
            </FormGrid>
          </Dropdown>
        );
      })}
      <div className="mb-8" />
    </FlexWrapper>
  );
};

export default NotificationContent;
